import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { useVisitorStore } from '@/stores/visitorStore';
import { useAnalytics } from '@/hooks/useAnalytics';
import type { SectionProps } from '@/types';

const SERVICE_OPTIONS = [
  { value: 'website', label: 'Business website' },
  { value: 'digital_marketing', label: 'Digital marketing' },
  { value: 'qr_code', label: 'QR code setup' },
  { value: 'photography', label: 'Product photography' },
];

const BUDGET_OPTIONS = [
  { value: 'under_10k', label: 'Under ₹10,000' },
  { value: '10k_50k', label: '₹10,000 – ₹50,000' },
  { value: '50k_1l', label: '₹50,000 – ₹1 Lakh' },
  { value: '1l_5l', label: '₹1 Lakh – ₹5 Lakh' },
  { value: 'above_5l', label: 'Above ₹5 Lakh' },
];

export function LeadFormSection({ business, config }: SectionProps) {
  const title = (config.title as string) || 'Get in touch';
  const subtitle =
    (config.subtitle as string) ||
    'Interested in our services or want to list your business? Leave your details.';

  const visitorFingerprint = useVisitorStore((s) => s.visitorFingerprint);
  const { track } = useAnalytics(business.id);

  const [isBusinessOwner, setIsBusinessOwner] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [services, setServices] = useState<string[]>([]);
  const [budgetRange, setBudgetRange] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiClient<{ id: string; status: string }>(
        `/public/businesses/${business.id}/leads`,
        { method: 'POST', body },
      ),
    onSuccess: () => {
      setSubmitted(true);
      track('lead_submitted');
    },
  });

  const toggleService = (value: string) => {
    setServices((prev) =>
      prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value],
    );
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      is_business_owner: isBusinessOwner,
      business_type: businessType || undefined,
      services_interested: services,
      name,
      phone,
      email: email || undefined,
      budget_range: budgetRange || undefined,
      visitor_fingerprint: visitorFingerprint || undefined,
    });
  };

  if (submitted) {
    return (
      <section className="section-card text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-primary/10 text-brand-primary">
          <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <h2 className="mt-4 text-xl font-semibold text-slate-900">We received your enquiry</h2>
        <p className="mt-2 text-slate-600">Our team will contact you shortly.</p>
      </section>
    );
  }

  return (
    <section className="section-card">
      <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
      <p className="mt-1 text-sm text-slate-500">{subtitle}</p>

      <form onSubmit={handleSubmit} className="mt-6 space-y-5">
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={isBusinessOwner}
            onChange={(e) => setIsBusinessOwner(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-brand-primary focus:ring-brand-primary"
          />
          <span className="text-sm text-slate-700">I am a business owner looking for QRBusiness services</span>
        </label>

        {isBusinessOwner && (
          <>
            <div>
              <label htmlFor="businessType" className="block text-sm font-medium text-slate-700">
                Business type
              </label>
              <input
                id="businessType"
                type="text"
                value={businessType}
                onChange={(e) => setBusinessType(e.target.value)}
                placeholder="e.g. Clothing retail, Restaurant"
                className="mt-1 w-full rounded-brand border border-slate-300 px-3 py-2 text-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
              />
            </div>

            <div>
              <span className="block text-sm font-medium text-slate-700">Services interested in</span>
              <div className="mt-2 flex flex-wrap gap-2">
                {SERVICE_OPTIONS.map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => toggleService(value)}
                    className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                      services.includes(value)
                        ? 'bg-brand-primary text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="budget" className="block text-sm font-medium text-slate-700">
                Budget range
              </label>
              <select
                id="budget"
                value={budgetRange}
                onChange={(e) => setBudgetRange(e.target.value)}
                className="mt-1 w-full rounded-brand border border-slate-300 px-3 py-2 text-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
              >
                <option value="">Select budget</option>
                {BUDGET_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="leadName" className="block text-sm font-medium text-slate-700">
              Name *
            </label>
            <input
              id="leadName"
              type="text"
              required
              minLength={2}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full rounded-brand border border-slate-300 px-3 py-2 text-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
            />
          </div>
          <div>
            <label htmlFor="leadPhone" className="block text-sm font-medium text-slate-700">
              Phone *
            </label>
            <input
              id="leadPhone"
              type="tel"
              required
              minLength={10}
              maxLength={15}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="10-digit mobile"
              className="mt-1 w-full rounded-brand border border-slate-300 px-3 py-2 text-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
            />
          </div>
        </div>

        <div>
          <label htmlFor="leadEmail" className="block text-sm font-medium text-slate-700">
            Email (optional)
          </label>
          <input
            id="leadEmail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded-brand border border-slate-300 px-3 py-2 text-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
          />
        </div>

        {mutation.isError && (
          <p className="text-sm text-red-600">
            {(mutation.error as Error).message || 'Failed to submit. Please try again.'}
          </p>
        )}

        <Button type="submit" isLoading={mutation.isPending} disabled={!name || !phone}>
          Send enquiry
        </Button>
      </form>
    </section>
  );
}
